/* eslint-disable no-console */
import React, { Component } from 'react';
import fetch from 'cross-fetch'; // isomorphic fetch for different browsers
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import Input from '../Input';
import PlayButtonColorSelect from '../PlayButtonColorSelect';
import InputValidator from '../InputValidator';
import resizeImage from '../../common/resizeImage';
import uploadImageAndFetchLink from '../../common/uploadImage';
import isTestingEnvironment from '../../common/isTestingEnvironment';
import eventService from '../../common/eventService';
import handleKeyPress from '../../common/handleKeyPress';
import configs from '../../constants/configs';
import rules from '../../constants/businessRules/businessRules';
import HorizontalRule from '../HorizontalRule';
import BananatagButton from '../BananatagButton';
import * as Styled from '../styles';
import {
    ERR_IMAGE_UPLOAD_FILE_TOO_BIG,
    ERR_IMAGE_UPLOAD_RESIZE,
    ERR_IMAGE_UPLOAD_READ,
    ERR_VIDEO_UPLOAD_THUMBNAIL_GENERIC,
    ERR_VIDEO_UPLOAD_THUMBNAIL_LINK_INVALID,
    ERR_VIDEO_UPLOAD_THUMBNAIL_LINK_YOUTUBE,
    ERR_VIDEO_UPLOAD_THUMBNAIL_PRIVATE,
    ERR_VIDEO_UPLOAD_THUMBNAIL_UPLOAD,
    ERR_VIDEO_UPLOAD_THUMBNAIL_GENERATE,
} from '../../constants/Errors';

const ValidatedLink = InputValidator(Input, { url: 1 });

const _fetchYoutubeThumbnail = (videoUrl) =>
    new Promise((resolve, reject) => {
        (async () => {
            try {
                if (!videoUrl) {
                    reject(new Error(ERR_VIDEO_UPLOAD_THUMBNAIL_LINK_INVALID));
                    return;
                }
                const videoID = videoUrl
                    .replace(/(>|<)/gi, '')
                    .split(
                        /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/i
                    )[1];
                const thumbnailEndpoint = `https://www.googleapis.com/youtube/v3/videos?key=${configs.youtubeAPIKey}&part=snippet&id=${videoID}`;
                const response = await fetch(thumbnailEndpoint);
                const data = await response.json();
                const thumbnails = _get(data, 'items[0].snippet.thumbnails', null);
                if (!thumbnails) {
                    reject(new Error(ERR_VIDEO_UPLOAD_THUMBNAIL_PRIVATE));
                    return;
                }
                const thumbnailURL =
                    _get(thumbnails, 'maxres.url', null) ||
                    _get(thumbnails, 'high.url', null) ||
                    _get(thumbnails, 'default.url', null) ||
                    `https://img.youtube.com/vi/${videoID}/0.jpg`;
                if (!thumbnailURL) {
                    reject(new Error(ERR_VIDEO_UPLOAD_THUMBNAIL_LINK_YOUTUBE));
                    return;
                }
                resolve(thumbnailURL);
            } catch (err) {
                reject(err);
            }
        })();
    });

class VideoInput extends Component {
    state = {
        isSourceOnYoutube: false,
        isValidLink: false,
        imageData: '',
        uploadedImageSrc: '',
        enteredHref: '',
    };

    componentDidUpdate(prevProps) {
        const { index } = prevProps;
        const { index: newIndex } = this.props;

        // when the user clicks on another video element, we need to reset local state so we do not "carry" values from the first element
        if (index !== newIndex) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                isSourceOnYoutube: false,
                isValidLink: false,
                imageData: '',
                uploadedImageSrc: '',
                enteredHref: '',
            });
        }
    }

    onClearButtonClick = () => {
        const { onDelete } = this.props;

        this.setState({
            isSourceOnYoutube: false,
            imageData: '',
            uploadedImageSrc: '',
            enteredHref: '',
        });

        onDelete();
    };

    onApplyButtonClick = async () => {
        const { onChange } = this.props;
        const { isValidLink, enteredHref } = this.state;

        if (!isValidLink) {
            return;
        }

        const isSourceOnYoutube = await RegExp(
            /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/
        ).test(enteredHref);
        if (isSourceOnYoutube) {
            this.setState({ isSourceOnYoutube: true });
        }
        onChange({ href: enteredHref });
        this._upload();
    };

    onPlayButtonClick = (selectedOption) => {
        const { onChange, playButtonColor } = this.props;

        if (selectedOption === playButtonColor) {
            return;
        }

        onChange({ playButtonColor: selectedOption });
    };

    _uploadImage = (event) => {
        const { index, onChange } = this.props;
        const imageFile = event.target.files[0];
        if (!imageFile || imageFile.type === 'image/gif') {
            return;
        }
        if (imageFile.size / 1024 / 1024 > rules.MAX_IMAGE_UPLOAD_FILE_SIZE) {
            eventService.notify(ERR_IMAGE_UPLOAD_FILE_TOO_BIG, true);
            return;
        }
        const reader = new window.FileReader();
        reader.readAsDataURL(imageFile);
        reader.onload = async () => {
            try {
                // we are not enabling raw images for thumbnails since play button watermark would not work due to AWS Lambda payload limitation
                const imageData = await resizeImage(reader.result, imageFile.type);

                const fileUploadInput = document.getElementById(index);
                fileUploadInput.value = null; // so that the input onChange event gets fired if user uploads the same image twice
                this.setState({ imageData });
                this._generateThumbnail(onChange);
            } catch (err) {
                console.error(err);
                eventService.notify(ERR_IMAGE_UPLOAD_RESIZE, true);
            }
        };
        reader.onerror = (err) => {
            console.error(err);
            eventService.notify(ERR_IMAGE_UPLOAD_READ, true);
        };
    };

    _onValidation = (validation) => {
        const { isValid } = validation;
        this.setState({ isValidLink: isValid });
    };

    _upload = async () => {
        const { onChange, playButtonColor } = this.props;
        const { isSourceOnYoutube, enteredHref } = this.state;
        const TEMP_IMAGE = 'https://storage-images.bananatag.com/qp94HD/c985d5c8875bf113c617bb009c4409e3.png';

        if (isSourceOnYoutube) {
            let thumbnailImageUrl;
            try {
                thumbnailImageUrl = await _fetchYoutubeThumbnail(enteredHref);
            } catch (err) {
                console.error(err);
                if (err.message === 'private') {
                    eventService.notify(ERR_VIDEO_UPLOAD_THUMBNAIL_PRIVATE, true);
                    this.setState({ isSourceOnYoutube: false });
                    return;
                }
                eventService.notify(ERR_VIDEO_UPLOAD_THUMBNAIL_GENERIC, true);
                return;
            }

            try {
                this.setState({
                    // IE11: For YouTube thumbnails, imageSuffix should be 'jpeg' and not 'jpg'
                    uploadedImageSrc: await uploadImageAndFetchLink(
                        { imageUrl: thumbnailImageUrl, imageSuffix: 'jpeg' },
                        true
                    ),
                });
            } catch (err) {
                if (isTestingEnvironment()) {
                    this.setState({ uploadedImageSrc: TEMP_IMAGE });
                } else {
                    console.error(err);
                    eventService.notify(
                        err.customMessage ? err.customMessage : ERR_VIDEO_UPLOAD_THUMBNAIL_UPLOAD,
                        true
                    );
                }
            }

            const { uploadedImageSrc } = this.state;
            onChange({ rawSource: uploadedImageSrc, playButtonColor });
        }
    };

    async _generateThumbnail(onChange) {
        const { playButtonColor } = this.props;
        const { imageData } = this.state;
        const TEMP_IMAGE = 'https://storage-images.bananatag.com/qp94HD/c985d5c8875bf113c617bb009c4409e3.png';

        try {
            this.setState({ uploadedImageSrc: await uploadImageAndFetchLink({ imageData }, false) });
        } catch (err) {
            if (isTestingEnvironment()) {
                this.setState({ uploadedImageSrc: TEMP_IMAGE });
            } else {
                console.error(err);
                eventService.notify(err.customMessage ? err.customMessage : ERR_VIDEO_UPLOAD_THUMBNAIL_GENERATE, true);
            }
        }
        const { uploadedImageSrc } = this.state;
        onChange({ rawSource: uploadedImageSrc, playButtonColor });
    }

    render() {
        const { index, href, rawSource, playButtonColor } = this.props;
        const { isValidLink, enteredHref, uploadedImageSrc } = this.state;
        const imageSrcUploaded = uploadedImageSrc.length > 0;

        const clearButton = (
            <div
                style={{
                    width: 28,
                    height: 28,
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: '#0277BD',
                    cursor: 'pointer',
                }}
                onClick={() => this.onClearButtonClick()}
                onKeyDown={(e) => handleKeyPress(e, this.onClearButtonClick)}
                tabIndex={0}
                role="button"
                aria-label="Clear video link"
                data-test-id={`${index}-video-clear-btn`}
            >
                <span style={{ display: 'flex' }}>
                    <i
                        aria-hidden
                        className="material-icons"
                        style={{ color: 'white', fontSize: 18, marginLeft: 4, marginTop: 4 }}
                    >
                        close
                    </i>
                </span>
            </div>
        );
        const applyButton = (
            <div
                style={{
                    width: 28,
                    height: 28,
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: enteredHref.length > 0 && isValidLink ? '#0277BD' : '#D9DADB',
                    cursor: 'pointer',
                }}
                onClick={async () => this.onApplyButtonClick()}
                onKeyDown={async (e) => handleKeyPress(e, this.onApplyButtonClick)}
                tabIndex={0}
                role="button"
                aria-label="Confirm video link"
                data-test-id={`${index}-video-apply-btn`}
            >
                <span style={{ display: 'flex' }}>
                    <i
                        aria-hidden
                        className="material-icons"
                        style={{ color: 'white', fontSize: 18, marginLeft: 4, marginTop: 4 }}
                    >
                        done
                    </i>
                </span>
            </div>
        );

        return (
            <div style={{ width: '100%' }}>
                <Styled.Label>VIDEO LINK</Styled.Label>
                <div style={{ position: 'relative' }}>
                    {imageSrcUploaded || rawSource.length > 0 ? (
                        <>
                            <div
                                style={{
                                    boxSizing: 'border-box',
                                    border: '1px solid #0277BD',
                                    display: 'flex',
                                    maxWidth: 180,
                                    height: 28,
                                    padding: 5,
                                    marginBottom: 10,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: '11px',
                                        lineHeight: '11px',
                                        padding: 4,
                                        color: 'gray',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {href}
                                </span>
                            </div>
                            {clearButton}
                        </>
                    ) : (
                        <>
                            <ValidatedLink
                                type="text"
                                width={180}
                                value={enteredHref}
                                placeholder="Enter video URL here"
                                onValidation={this._onValidation}
                                border={enteredHref.length > 0 ? '1px solid #0277BD' : '1px solid #D9DADB'}
                                onChange={(event) => this.setState({ enteredHref: event.target.value })}
                            />
                            {applyButton}
                        </>
                    )}
                </div>
                {href.length > 0 && (
                    <div>
                        <HorizontalRule />
                        <Styled.Label>VIDEO THUMBNAIL</Styled.Label>
                        <div>
                            <div>
                                <Styled.LabelSmall>Preview Image</Styled.LabelSmall>
                                <BananatagButton
                                    file
                                    index={index}
                                    type="outline"
                                    size="small"
                                    text="Upload custom image"
                                    onChange={(e) => this._uploadImage(e)}
                                    styling={{ marginBottom: 10, paddingLeft: 16 }}
                                />
                            </div>
                            {(imageSrcUploaded || rawSource.length > 0) && (
                                <div>
                                    <Styled.LabelSmall>Play Button</Styled.LabelSmall>
                                    <PlayButtonColorSelect
                                        label="Play Button Color"
                                        playButtonColor={playButtonColor === 'white' ? 0 : 1}
                                        onClick={(selectedOption) => this.onPlayButtonClick(selectedOption)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

VideoInput.propTypes = {
    href: PropTypes.string.isRequired,
    index: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    playButtonColor: PropTypes.string.isRequired,
    rawSource: PropTypes.string.isRequired,
};

export default VideoInput;
